import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { RouteComponentProps } from "react-router";

import { PageHeader, PageRoot, Button, Icon } from "../../components";
import routes from "../../constants/routes";
import { Typography, TextField, Card } from "@material-ui/core";
import { RegulatoryInstrument } from "../../constants/interfaces";
import classNames from "../../utils/classNames";
import { useGetManual } from "../../utils/useApi";
import formatDate from "../../utils/formatDate";
import { palette } from "../../constants/palette";
import riType from "../../constants/riTyps";
import { OnBeforeUnload } from "../../components/navigation/OnBeforeUnload";

const useStyles = makeStyles(theme => ({
  button: {
    margin: theme.spacing(1)
  },
  error: {
    color: theme.palette.error.main
  },
  iconWrapper: {
    alignItems: "center",
    borderRadius: "50%",
    boxShadow: theme.shadows["4"],
    display: "flex",
    justifyContent: "center",
    height: "50px",
    marginRight: theme.spacing(2),
    width: "50px"
  },
  idField: {
    flexGrow: 1,
    minWidth: 300,
    margin: theme.spacing(1)
  },
  inputWrapper: {
    alignItems: "center",
    flexFlow: "row wrap",
    display: "flex"
  },
  resultContainer: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(4)
  },
  resultDetailItem: {
    margin: theme.spacing(1)
  },
  resultDetailWrapper: {
    width: "100%",
    position: "relative"
  },
  row: {
    display: "flex",
    flexFlow: "row wrap",
    width: "100%"
  },
  resultTitle: {
    alignItems: "center",
    display: "flex",
    justifyContent: "center"
  },
  resultSubtitle: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    textAlign: "center"
  },
  root: {
    alignItems: "center",
    display: "flex",
    flexFlow: "column"
  },
  success: {
    color: palette.SUCCESS
  }
}));

const RI_STATUS_MAPPER: any = {
  Active: "Active",
  "Provisionally Active": "Provisionally Active",
  "Active - Continuing in Force": "Active - Continuing in Force",
  "Surrender Initiated": "Active",
  "Cancellation Initiated": "Active",
  "Suspension Initiated": "Active"
};

const VerifyRI: React.FC<RouteComponentProps> = (props) => {
  const [regulatoryInstrumentId, setRegulatoryInstrumentId] = useState<string>(
    ""
  );
  const [legalEntityName, setLegalEntityName] = useState<string>("");
  const { reset, error, data: regulatoryInstrument, query } = useGetManual<
    RegulatoryInstrument | null | undefined
  >(undefined);

  const classes = useStyles();

  return (
    <PageRoot>
      <PageHeader
        label="Registers"
        breadcrumbLinks={[{ label: routes.HOME.label, route: routes.HOME.url }]}
      />
      {(regulatoryInstrumentId || legalEntityName) && <OnBeforeUnload />}
      <div className={classes.root}>
        <div>
          <Typography
            style={{
              textAlign: "center"
            }}
            component="h2"
            variant="h5"
          >
            Verify Regulatory Instrument
          </Typography>

          <div className={classes.inputWrapper}>
            <TextField
              className={classes.idField}
              id="legalEntity"
              label="Legal Entity Name"
              onChange={e => setLegalEntityName(e.target.value)}
              placeholder="Enter legal entity name"
              value={legalEntityName}
            />

            <TextField
              className={classes.idField}
              id="regulatoryInstrumentId"
              label="Regulatory Instrument number"
              onChange={e => setRegulatoryInstrumentId(e.target.value)}
              placeholder="Enter regulatory instrument number"
              value={regulatoryInstrumentId}
            />
            <Button
              className={classes.button}
              disabled={!regulatoryInstrumentId || !legalEntityName}
              id="verify"
              color="primary"
              onClick={e => {
                e.preventDefault();
                const trimRiId = regulatoryInstrumentId.trim();
                const trimLeName = legalEntityName.trim();
                const encodedLeName = encodeURIComponent(trimLeName);
                query(
                  encodeURI(`/ri/verify?legalEntity=${encodedLeName}&riNumber=${trimRiId}`),
                  `Unverified regulatory instrument number ${trimRiId}`
                );
                setRegulatoryInstrumentId("");
                setLegalEntityName("");
              }}
            >
              Verify
            </Button>
            {!!regulatoryInstrument && (
              <Button
                className={classes.button}
                id="clear"
                onClick={() => {
                  setRegulatoryInstrumentId("");
                  setLegalEntityName("");
                  reset();
                }}
              >
                Clear
              </Button>
            )}
            <Button
              onClick={() => {
                props.history.push(`${routes.HOME.url}`);
              }}
              id="close"
            >
              Close
            </Button>
          </div>
        </div>
        <Card className={classes.resultContainer}>
          {error ? (
            <div>
              <div className={classes.resultTitle}>
                <div className={classes.iconWrapper}>
                  <Icon
                    className={classes.error}
                    fontSize="large"
                    name="close"
                  />
                </div>
                <Typography variant="h3" className={classes.error}>
                  Invalid
                </Typography>
              </div>
              <Typography
                variant="subtitle1"
                className={classNames(classes.resultSubtitle, classes.error)}
              >
                {error.message}
              </Typography>
              <Typography>
                This regulatory instrument cannot be verified or is not valid
              </Typography>
            </div>
          ) : regulatoryInstrument ? (
            <div>
              <div className={classes.resultTitle}>
                <div className={classes.iconWrapper}>
                  <Icon
                    className={classes.success}
                    fontSize="large"
                    name="done"
                  />
                </div>
                <Typography variant="h3" className={classes.success}>
                  Valid
                </Typography>
              </div>
              <Typography
                variant="subtitle1"
                className={classNames(classes.resultSubtitle, classes.success)}
              >
                {`Verified ${regulatoryInstrument.riNumber}`}
              </Typography>
              <div className={classes.resultDetailWrapper}>
                <div className={classes.row}>
                  <div className={classes.resultDetailItem}>
                    <Typography color="textSecondary" variant="caption">
                      Legal Entity
                    </Typography>
                    <Typography>{regulatoryInstrument.legalEntity}</Typography>
                  </div>
                  <div className={classes.resultDetailItem}>
                    <Typography color="textSecondary" variant="caption">
                      Premises
                    </Typography>
                    <Typography>{regulatoryInstrument.premises}</Typography>
                  </div>
                  <div className={classes.resultDetailItem}>
                    <Typography color="textSecondary" variant="caption">
                      Regulatory instrument type
                    </Typography>
                    <Typography>
                      {(riType as any)[regulatoryInstrument.riType]}
                    </Typography>
                  </div>
                </div>
                <div className={classes.row}>
                  <div className={classes.resultDetailItem}>
                    <Typography color="textSecondary" variant="caption">
                      Regulatory instrument Status
                    </Typography>
                    <Typography>
                      {
                        RI_STATUS_MAPPER[
                          regulatoryInstrument.riStatus as string
                        ]
                      }
                    </Typography>
                  </div>
                  {regulatoryInstrument.riStartDate && (
                    <div className={classes.resultDetailItem}>
                      <Typography color="textSecondary" variant="caption">
                        Start Date
                      </Typography>
                      <Typography>
                        {formatDate.condensed(regulatoryInstrument.riStartDate)}
                      </Typography>
                    </div>
                  )}
                  {regulatoryInstrument.expiryDate && (
                    <div className={classes.resultDetailItem}>
                      <Typography color="textSecondary" variant="caption">
                        Expiry Date
                      </Typography>
                      <Typography>
                        {formatDate.condensed(regulatoryInstrument.expiryDate)}
                      </Typography>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <Typography color="primary" variant="subtitle1">
              Enter the regulatory instrument number and legal entity name to
              get started
            </Typography>
          )}
        </Card>
      </div>
    </PageRoot>
  );
};

export default VerifyRI;
